// Best-effort visitor geolocation via free public IP API.
// Cached in sessionStorage to avoid repeat lookups.

const CACHE_KEY = "padelcorner-visitor-geo";

const COUNTRY_NAMES: Record<string, string> = {
  LV: "Latvia", LT: "Lithuania", EE: "Estonia", GB: "United Kingdom",
  US: "United States", DE: "Germany", FR: "France", ES: "Spain",
  IT: "Italy", PL: "Poland", FI: "Finland", SE: "Sweden", NO: "Norway",
  DK: "Denmark", NL: "Netherlands", BE: "Belgium", IE: "Ireland",
  RU: "Russia", UA: "Ukraine", BY: "Belarus", CZ: "Czechia",
  AT: "Austria", CH: "Switzerland", PT: "Portugal", GR: "Greece",
};

export interface VisitorGeo {
  country: string | null;
  country_code: string | null;
}

let inflight: Promise<VisitorGeo> | null = null;

export function getCachedVisitorGeo(): VisitorGeo | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as VisitorGeo;
  } catch {
    return null;
  }
}

export async function resolveVisitorGeo(): Promise<VisitorGeo> {
  const cached = getCachedVisitorGeo();
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch("https://api.country.is/", { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) throw new Error("geo failed");
      const json = await res.json();
      const code: string | null = json?.country ?? null;
      const geo: VisitorGeo = {
        country_code: code,
        country: code ? (COUNTRY_NAMES[code] ?? code) : null,
      };
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(geo)); } catch {}
      return geo;
    } catch {
      const fallback: VisitorGeo = { country: null, country_code: null };
      return fallback;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

// Kick off resolution early (non-blocking)
if (typeof window !== "undefined") {
  setTimeout(() => { resolveVisitorGeo().catch(() => {}); }, 500);
}
